import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import useForm from 'assets/components/form/hooks/Form';
import TextInput from 'assets/components/inputs/text/TextInput';
import { getLocales } from 'assets/locales/Locale';
import { useEffect, useMemo } from 'react';
import ClaimDocumentPreview from '../../claim/other/Claim.DocumentPreview';
import { useClaimEdit } from '../ClaimEdit.Hooks';
import Theme from 'assets/themes/Theme.Common';
import claimInvoiceSaveApi from 'models/claims/claimInvoice/save/Api.ClaimInvoice.Save';
import claimInvoiceDeleteApi from 'models/claims/claimInvoice/delete/Api.ClaimInvoice.Delete';
import { HttpStatus } from 'config/Api.Config';
import { addLog } from 'assets/components/feedback/Feedback';
import FileInputSimple from 'assets/components/inputs/file/FileInputSimple';
import InputWrapper from 'assets/components/inputs/wrappers/InputWrapper';
import DateInput from 'assets/components/inputs/dateInput/DateInput';
import dayjs from 'dayjs';
import { defaultDateFormat, defaultInputDateFormat, defaultServerDateFormat } from 'assets/utils/data/Date';

export default function ClaimEditInvoices() {
  const { lang } = getLocales();
  const { reload, claim, isClaimAdmin, isClaimInvoiceEditableByStatus, permissions, isClaimOwner } = useClaimEdit();

  const docForm = useForm<Utils.FormData<Model.IClaimInvoice>>({
    default: { claimId: claim?.id },
    onSubmit: async (data) => {
      if (data.formFile) {
        const res = await claimInvoiceSaveApi(data);
        if (res.status !== HttpStatus.ok) {
          addLog({
            error: 'We were unable to upload document',
          });
        }
      }
      docForm.reload();
      reload();
    },
    validation: (data, errors) => {
      if (!data.name) errors.name = lang.required;
    },
  });

  useEffect(() => {
    if (docForm?.data?.claimId !== claim?.id) {
      docForm.set({ claimId: claim?.id });
    }
  }, [claim?.id]);

  const onClaimInvoiceDelete = async (invoiceId: number) => {
    await claimInvoiceDeleteApi({ id: claim?.id, invoiceId });
    reload();
  };

  const uploadedFile = useMemo(
    () =>
      !docForm.data?.formFile
        ? null
        : {
            original: docForm.data.formFile,
            type: docForm.data.formFile.type as Utils.File.MimeType,
            dataUrl: window.URL.createObjectURL(docForm.data.formFile),
            name: docForm.data.name,
            notes: docForm.data.notes,
          },
    [docForm.data.formFile]
  );

  const isDisabled =
    isClaimInvoiceEditableByStatus ||
    docForm.hasErrors ||
    docForm.isSubmitted ||
    !docForm.isUpdated ||
    !docForm.data?.formFile;

  return (
    <>
      <div style={{ marginLeft: Theme.Size.XL, marginRight: Theme.Size.XL }}>
        <h2>{lang.invoices}</h2>
        <br />
        <InputWrapper>
          <FileInputSimple
            name="file"
            value={uploadedFile}
            onChange={({ file }) => {
              docForm.update({
                formFile: file.original,
                name: file.original?.name,
              });
            }}
            label={lang.documentUpload}
            disabled={
              isClaimInvoiceEditableByStatus || !(isClaimAdmin || (isClaimOwner && permissions?.UPLOAD_CLAIMINVOICE))
            }
          />
          <TextInput
            name="name"
            label={lang.name}
            value={docForm.data?.name}
            error={docForm.errors?.name}
            onChange={docForm.update}
            disabled={
              !uploadedFile ||
              isClaimInvoiceEditableByStatus ||
              !(isClaimAdmin || (isClaimOwner && permissions?.UPLOAD_CLAIMINVOICE))
            }
          />
          <TextInput
            name="invoiceNumber"
            label={lang.invoiceNumber}
            value={docForm.data?.invoiceNumber}
            error={docForm.errors?.invoiceNumber}
            onChange={docForm.update}
            disabled={
              !uploadedFile ||
              isClaimInvoiceEditableByStatus ||
              !(isClaimAdmin || (isClaimOwner && permissions?.UPLOAD_CLAIMINVOICE))
            }
          />
          <DateInput
            name="invoiceDate"
            label={lang.invoiceDate}
            value={docForm.data?.invoiceDate}
            error={docForm.errors?.invoiceDate}
            onChange={docForm.update}
            isLocal
            dateTimeFormat={defaultInputDateFormat}
            disabled={
              !uploadedFile ||
              isClaimInvoiceEditableByStatus ||
              !(isClaimAdmin || (isClaimOwner && permissions?.UPLOAD_CLAIMINVOICE))
            }
          />
          <TextInput
            name="notes"
            label={lang.notes}
            value={docForm.data?.notes}
            onChange={docForm.update}
            disabled={
              !uploadedFile ||
              isClaimInvoiceEditableByStatus ||
              !(isClaimAdmin || (isClaimOwner && permissions?.UPLOAD_CLAIMINVOICE))
            }
          />
          <Button
            render={lang.upload}
            onClick={docForm.submit}
            disabled={isDisabled}
            active={!docForm.hasErrors}
            class={ThemeButtonCircle}
            style={{ flexShrink: 0 }}
          />
        </InputWrapper>
        <br />
      </div>
      <ClaimDocumentPreview
        documents={claim?.claimInvoices?.map((it) => ({
          id: it.id,
          url: it.mediaFile.url,
          name: it.name,
          extension: it.mediaFile?.extension,
          mimeType: it.mediaFile?.contentType,
          storedFileName: it.mediaFile?.name,
          description: it.notes,
          info: lang.uploaded,
          inoviceNumber: it.invoiceNumber,
          invoiceDate: dayjs(it.invoiceDate, defaultServerDateFormat).format(defaultDateFormat),
          documentType: lang.invoice,
          onDelete: () => onClaimInvoiceDelete(it.id),
        }))}
        showGallery={true}
        isDeleteDisabled={
          isClaimInvoiceEditableByStatus || !(isClaimAdmin || (isClaimOwner && permissions?.DELETE_CLAIMINVOICE))
        }
        isDownloadDisabled={!(isClaimAdmin || (isClaimOwner && permissions?.DOWNLOAD_CLAIMINVOICE))}
      />
    </>
  );
}
